import { type ChartOptions } from "chart.js";

import { type User } from "@internal/user";
import {
  convertStartDateToTimeZone,
  formatting,
  type CurrencyCode,
} from "@north-beam/nb-common";

import { type DaySelection } from "../types";

import { type DataPoint } from "./types";

function getCssVariable(name: string, from: HTMLElement = document.body) {
  return getComputedStyle(from).getPropertyValue(name).trim();
}

export function getChartOptions({
  code,
}: {
  code: CurrencyCode;
}): ChartOptions<any> {
  const scales: Record<string, any> = {
    x: {
      grid: {
        drawOnChartArea: true,
        drawBorder: false,
        drawTicks: false,
        color: getCssVariable("--v2-border-tertiary"),
        borderDash: [2, 2],
      },
      ticks: {
        padding: 13,
        maxRotation: 45,
        autoSkip: true,
        color: getCssVariable("--v2-text-primary"),
        font: {
          size: 10,
        },
        callback: (value: string) => {
          return value;
        },
      },
      type: "timeseries",
      time: {
        unit: "month",
        displayFormats: {
          month: "MMM",
        },
      },
    },
    y: {
      position: "left",
      beginAtZero: true,
      grid: {
        drawOnChartArea: true,
        drawBorder: false,
        drawTicks: false,
        color: (context) => {
          if (
            context.index === 0 ||
            context.index === context.chart.scales.y.ticks.length - 1
          ) {
            return "";
          }
          return getCssVariable("--v2-border-tertiary");
        },
      },
      ticks: {
        maxTicksLimit: 5,
        padding: 6,
        color: getCssVariable("--v2-text-primary"),
        font: {
          size: 10,
        },
        callback: (value: number | string) => {
          return formatting.formatCurrency.approximate(Number(value), { code });
        },
      },
    },
  };

  return {
    responsive: true,
    maintainAspectRatio: false,
    parsing: false,
    animations: false,
    interaction: {
      mode: "index" as const,
      intersect: false,
      position: "nearest",
    },
    plugins: {
      tooltip: {
        enabled: false,
      },
      legend: {
        display: false,
      },
    },
    barThickness: "flex",
    minBarLength: 3,
    layout: {
      padding: { bottom: -8 },
    },
    scales,
  };
}

export function generateChartData({
  rows,
  timezone,
}: {
  rows: DaySelection[];
  timezone: User["timezone"];
}) {
  const labels = rows.map((e) => convertStartDateToTimeZone(e.day, timezone));
  const data: DataPoint[] = [];

  for (const [index, row] of rows.entries()) {
    data.push({
      x: labels[index],
      y: row.rev,
      day: row.day,
      isExcluded: row.isExcluded,
      willBeExcluded: row.willBeExcluded,
      exclusionReason: row.exclusionReason,
    });
  }

  return { labels: labels, datasets: [{ data }] };
}

export function generateChartDataset(
  data: ReturnType<typeof generateChartData>,
) {
  const datasets = [];

  for (const dataset of data.datasets) {
    const singleDataPoint = dataset.data.length === 1;
    const color = "#50BA58";
    const colorExcluded = "#E6EDEA";
    const colorWillBeExcluded = "#E6EDEA";
    const backgroundColor = dataset.data.map((e) =>
      e.willBeExcluded
        ? colorWillBeExcluded
        : e.isExcluded
        ? colorExcluded
        : color,
    );

    datasets.push({
      ...dataset,
      id: 0,
      yAxisID: "y",
      pointRadius: singleDataPoint ? 3 : 0,
      pointHoverRadius: 5,
      pointHitRadius: 100,
      backgroundColor,
      hoverBackgroundColor: backgroundColor,
      hoverBorderColor: backgroundColor,
      maxBarThickness: 9,
      borderRadius: 5,
      labelColor: color,
    });
  }

  return { ...data, datasets };
}
